import { ActionType } from 'typesafe-actions'
import {
  preMakingTemplatesActions,
  preMakingTemplatesActionType,
} from './actions'
import {
  PreMakingTemplate,
  PreMakingTemplateDetail,
  CustomTemplateSchedule,
  PreviousDayOrderCountsForTemplateResponse,
} from './pre-making'

export interface PreMakingTemplatesState {
  readonly preMakingTemplates: PreMakingTemplate[]
  readonly templateDetail: PreMakingTemplateDetail | null
  readonly customTemplateSchedules: CustomTemplateSchedule[]
  readonly selectedStationId: number | null
  readonly selectedStationName: string | null
  readonly error: Error
  readonly isLoading: boolean
  readonly previousDayOrderCounts: PreviousDayOrderCountsForTemplateResponse | null
  readonly isLoadingPreviousDayOrderCounts: boolean
  readonly previousDayOrderCountsError: Error | null
}

export const initialState: PreMakingTemplatesState = {
  preMakingTemplates: [],
  templateDetail: null,
  customTemplateSchedules: [],
  selectedStationId: null,
  selectedStationName: null,
  isLoading: false,
  error: null,
  previousDayOrderCounts: null,
  isLoadingPreviousDayOrderCounts: false,
  previousDayOrderCountsError: null,
}

export const preMakingTemplatesReducer = (
  state: PreMakingTemplatesState = initialState,
  action: ActionType<typeof preMakingTemplatesActions>
) => {
  switch (action.type) {
    case preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATES:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATES_SUCCEEDED:
      return {
        ...state,
        isLoading: false,
        error: null,
        preMakingTemplates: action.payload.preMakingTemplates,
        customTemplateSchedules: action.payload.customTemplateSchedules || [],
      }
    case preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATE_DETAIL:
      return {
        ...state,
        isLoading: true,
        error: null,
        previousDayOrderCounts: null,
        previousDayOrderCountsError: null,
      }
    case preMakingTemplatesActionType.FETCH_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED:
      return {
        ...state,
        isLoading: false,
        error: null,
        templateDetail: action.payload.templateDetail,
        previousDayOrderCounts: null,
        previousDayOrderCountsError: null,
      }
    case preMakingTemplatesActionType.FETCH_PREVIOUS_DAY_ORDER_COUNTS:
      return {
        ...state,
        isLoadingPreviousDayOrderCounts: true,
        previousDayOrderCountsError: null,
        previousDayOrderCounts: null,
      }
    case preMakingTemplatesActionType.FETCH_PREVIOUS_DAY_ORDER_COUNTS_SUCCEEDED:
      return {
        ...state,
        isLoadingPreviousDayOrderCounts: false,
        previousDayOrderCountsError: null,
        previousDayOrderCounts: action.payload.response,
      }
    case preMakingTemplatesActionType.FETCH_PREVIOUS_DAY_ORDER_COUNTS_FAILED:
      return {
        ...state,
        isLoadingPreviousDayOrderCounts: false,
        previousDayOrderCountsError: action.payload.error,
      }
    case preMakingTemplatesActionType.UPDATE_PRE_MAKING_TEMPLATE_DETAIL:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case preMakingTemplatesActionType.UPDATE_PRE_MAKING_TEMPLATE_DETAIL_SUCCEEDED:
      return {
        ...state,
        isLoading: false,
        error: null,
      }
    case preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_ACTIVE:
    case preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_INACTIVE:
    case preMakingTemplatesActionType.CREATE_PRE_MAKING_TEMPLATE:
    case preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_ACTIVE_SUCCEEDED:
    case preMakingTemplatesActionType.MAKE_PRE_MAKING_TEMPLATE_INACTIVE_SUCCEEDED:
      return {
        ...state,
        isLoading: false,
        error: null,
        preMakingTemplates: state.preMakingTemplates.map(template =>
          template.id === action.payload.templateId
            ? { ...template, isActive: action.payload.isActive }
            : template
        ),
      }
    case preMakingTemplatesActionType.CREATE_PRE_MAKING_TEMPLATE_SUCCEEDED:
      return {
        ...state,
        isLoading: false,
        error: null,
        preMakingTemplates: [
          ...state.preMakingTemplates,
          action.payload.template,
        ],
      }
    case preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        error: null,
        preMakingTemplates: state.preMakingTemplates.filter(
          template => template.id !== action.payload.templateId
        ),
      }
    case preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
      }
    case preMakingTemplatesActionType.DELETE_CUSTOM_TEMPLATE_CANCELLED:
      return {
        ...state,
        isLoading: false,
        error: null,
      }
    case preMakingTemplatesActionType.SET_SELECTED_STATION:
      return {
        ...state,
        selectedStationId: action.payload.stationId,
        selectedStationName: action.payload.stationName,
      }
    case preMakingTemplatesActionType.THROW_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
      }
    default:
      return state
  }
}
